// src/components/patient/Home/PostCard.jsx
import React from "react";

const isVideoUrl = (url = "") =>
  /\.(mp4|webm|ogg)(\?|$)/i.test(url);

/* ------------ MAIN CARD ------------ */

const PostCard = ({
  post,
  user,
  activeTab,
  isExpanded,
  onToggleExpanded,
  likes,
  comments,
  commentCount,
  showComments,
  onToggleComments,
  onToggleLike,
  onFollowDoctor,
  onDoctorClick,
  likeLoading,
  followLoading,
  isFollowing,
  onShare,
  newComment,
  commentLoading,
  onChangeComment,
  onAddComment,
  onDeleteComment,
  onCommentKeyPress,
  views,
}) => {
  const handleImageError = (e) => {
    e.target.src =
      "https://via.placeholder.com/48/4F46E5/FFFFFF?text=Dr";
  };

  const files = Array.isArray(post.files) ? post.files : [];
  const hasMedia = files.length > 0;

  // carousel state
  const [activeIndex, setActiveIndex] = React.useState(0);
const videoRef = React.useRef(null);
const [muted, setMuted] = React.useState(true);

const handleToggleMute = (e) => {
  e.stopPropagation();
  const v = videoRef.current;
  if (!v) return;

  const next = !muted;
  v.muted = next;
  setMuted(next);
};

  const cardRef = React.useRef(null);
  const [isCardInView, setIsCardInView] = React.useState(false);

  React.useEffect(() => {
    // reset index if post changes
    setActiveIndex(0);
  }, [post.id]);

  const activeUrl = hasMedia ? files[activeIndex] : null;
  const activeIsVideo = activeUrl && isVideoUrl(activeUrl);

  // 🔍 Observe card visibility
  React.useEffect(() => {
    const el = cardRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.target !== el) return;
          const inView = entry.isIntersecting;
          setIsCardInView(inView);

          // If it just went out of view, pause immediately
          if (!inView && videoRef.current) {
            try {
              videoRef.current.pause();
            } catch {
              // ignore
            }
          }
        });
      },
      {
        threshold: 0.3, // ~30% of card must be visible to count as "in view"
      }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // 🎬 Autoplay / pause when in view + slide changes
  React.useEffect(() => {
    const v = videoRef.current;
    if (!v) return;

    if (isCardInView && activeIsVideo) {
      const playPromise = v.play();
      if (playPromise && typeof playPromise.then === "function") {
        playPromise.catch(() => {
          // Autoplay might be blocked, ignore
        });
      }
    } else {
      try {
        v.pause();
      } catch {
        // ignore
      }
    }
  }, [isCardInView, activeIsVideo, activeUrl]);

  const goPrev = (e) => {
    e.stopPropagation();
    if (!hasMedia) return;
    setActiveIndex((idx) =>
      idx === 0 ? files.length - 1 : idx - 1
    );
  };

  const goNext = (e) => {
    e.stopPropagation();
    if (!hasMedia) return;
    setActiveIndex((idx) =>
      idx === files.length - 1 ? 0 : idx + 1
    );
  };

  return (
    <article
      ref={cardRef}
      style={{
        background: "#FFFFFF",
        borderRadius: 16,
        overflow: "hidden",
        boxShadow: "0 4px 14px rgba(15,23,42,0.08)",
        border: "1px solid rgba(226, 232, 240, 0.9)",
        position: "relative",
      }}
    >
      {/* Top badge when needed */}
      {activeTab === "home" && isFollowing && (
        <div
          style={{
            position: "absolute",
            top: 10,
            right: 10,
            zIndex: 3,
            background: "rgba(79, 70, 229, 0.12)",
            color: "#4F46E5",
            padding: "3px 8px",
            borderRadius: 999,
            fontSize: 11,
            fontWeight: 600,
          }}
        >
          ✓ Following
        </div>
      )}

      {/* Header – avatar + name + follow */}
      <DoctorHeader
        post={post}
        user={user}
        activeTab={activeTab}
        onDoctorClick={onDoctorClick}
        onFollowDoctor={onFollowDoctor}
        followLoading={followLoading}
        isFollowing={isFollowing}
        handleImageError={handleImageError}
      />

      {/* Media area (image / video / carousel) */}
      {/* Media area (image / video / carousel) */}
{hasMedia && (
  <div
    style={{
      position: "relative",
      width: "100%",
      background: "#000",
    }}
  >
    {/* Active slide */}
    {activeIsVideo ? (
      <video
        ref={videoRef}
        src={activeUrl}
        muted={muted}
        loop
        playsInline
        autoPlay={isCardInView}   // stays as you already had
        controls={false}          // hide native controls
        onClick={() => {          // tap to pause/play
          const v = videoRef.current;
          if (!v) return;
          if (v.paused) v.play();
          else v.pause();
        }}
        style={{
          display: "block",
          width: "100%",
          maxHeight: 420,
          objectFit: "cover",
          outline: "none",
        }}
      />
    ) : (
      <img
        src={activeUrl}
        alt={post.title || "Medical post"}
        onError={(e) => {
          e.target.style.display = "none";
        }}
        style={{
          display: "block",
          width: "100%",
          maxHeight: 420,
          objectFit: "cover",
        }}
      />
    )}

    {/* 🔊 Mute / Unmute button */}
    {activeIsVideo && (
      <button
        type="button"
        onClick={handleToggleMute}
        style={{
          position: "absolute",
          bottom: 10,
          left: 10,
          padding: "4px 8px",
          borderRadius: 999,
          border: "none",
          fontSize: 12,
          fontWeight: 600,
          background: "rgba(15,23,42,0.75)",
          color: "#F9FAFB",
          cursor: "pointer",
        }}
      >
        {muted ? "🔇" : "🔊"}
      </button>
    )}

    {/* Carousel controls when multiple files */}
    {files.length > 1 && (
      <>
        {/* Left arrow */}
        <button
          type="button"
          onClick={goPrev}
          style={{
            position: "absolute",
            top: "50%",
            left: 8,
            transform: "translateY(-50%)",
            width: 30,
            height: 30,
            borderRadius: 999,
            border: "none",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "rgba(15,23,42,0.55)",
            color: "#F9FAFB",
            cursor: "pointer",
          }}
        >
          ‹
        </button>

        {/* Right arrow */}
        <button
          type="button"
          onClick={goNext}
          style={{
            position: "absolute",
            top: "50%",
            right: 8,
            transform: "translateY(-50%)",
            width: 30,
            height: 30,
            borderRadius: 999,
            border: "none",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "rgba(15,23,42,0.55)",
            color: "#F9FAFB",
            cursor: "pointer",
          }}
        >
          ›
        </button>

        {/* Slide indicator */}
        <div
          style={{
            position: "absolute",
            bottom: 10,
            right: 10,
            padding: "3px 8px",
            borderRadius: 999,
            fontSize: 11,
            fontWeight: 600,
            background: "rgba(15,23,42,0.75)",
            color: "#E5E7EB",
          }}
        >
          {activeIndex + 1} / {files.length}
        </div>
      </>
    )}
  </div>
)}


      {/* Content */}
      <div
        style={{
          padding: "10px 14px 14px",
        }}
      >
        {/* Icons row */}
        <PostFooter
          likes={likes}
          commentCount={commentCount}
          showComments={showComments}
          onToggleLike={onToggleLike}
          onToggleComments={onToggleComments}
          onShare={onShare}
          likeLoading={likeLoading}
        />

        {/* Likes line – “123 likes” */}
        <div
          style={{
            marginTop: 4,
            fontSize: 13,
            fontWeight: 600,
            color: "#111827",
          }}
        >
          {likes.count} {likes.count === 1 ? "like" : "likes"}
        </div>

        {/* Description / caption */}
        <PostContent
          post={post}
          isExpanded={isExpanded}
          onToggleExpanded={onToggleExpanded}
        />

        {/* View comments line */}
        {commentCount > 0 && (
          <button
            onClick={onToggleComments}
            style={{
              marginTop: 4,
              padding: 0,
              background: "transparent",
              border: "none",
              fontSize: 13,
              color: "#6B7280",
              cursor: "pointer",
            }}
          >
            View {commentCount}{" "}
            {commentCount === 1 ? "comment" : "comments"}
          </button>
        )}

        {/* Meta line: views + date */}
        <div
          style={{
            marginTop: 4,
            fontSize: 11,
            color: "#9CA3AF",
          }}
        >
          {views > 0 && (
            <span style={{ fontWeight: 500 }}>
              {views} shikime
              <span style={{ margin: "0 4px" }}>•</span>
            </span>
          )}
          <span>
            {post.createdAt?.toLocaleDateString("sq-AL", {
              day: "2-digit",
              month: "short",
            }) || "Unknown"}
          </span>
        </div>

        {/* Comments block (when opened) */}
        {showComments && (
          <CommentsBlock
            comments={comments}
            user={user}
            post={post}
            newComment={newComment}
            commentLoading={commentLoading}
            onChangeComment={onChangeComment}
            onAddComment={onAddComment}
            onDeleteComment={onDeleteComment}
            onCommentKeyPress={onCommentKeyPress}
          />
        )}
      </div>
    </article>
  );
};

/* ------------ Doctor Header (top row) ------------ */

const DoctorHeader = ({
  post,
  user,
  activeTab,
  onDoctorClick,
  onFollowDoctor,
  followLoading,
  isFollowing,
  handleImageError,
}) => {
  const [isHovered, setIsHovered] = React.useState(false);
  const [isFollowHovered, setIsFollowHovered] =
    React.useState(false);

  return (
    <div
      style={{
        padding: "10px 14px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 8,
      }}
    >
      <div
        onClick={() => onDoctorClick(post.doctorId)}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        style={{
          display: "flex",
          alignItems: "center",
          cursor: "pointer",
          flex: 1,
        }}
      >
        <div style={{ position: "relative", marginRight: 10 }}>
          <img
            src={post.doctorPhoto}
            alt={`Dr. ${post.doctorName}`}
            onError={handleImageError}
            style={{
              width: 42,
              height: 42,
              borderRadius: "999px",
              objectFit: "cover",
              border: "2px solid #fff",
              boxShadow: isHovered
                ? "0 0 0 2px rgba(59,130,246,0.6)"
                : "0 2px 6px rgba(15,23,42,0.18)",
              transition: "box-shadow 0.2s ease",
            }}
          />
        </div>
        <div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <span
              style={{
                fontSize: 14,
                fontWeight: 600,
                color: "#111827",
              }}
            >
              Dr. {post.doctorName}
            </span>
            <span
              style={{
                fontSize: 11,
                padding: "2px 6px",
                borderRadius: 999,
                background: "rgba(59,130,246,0.06)",
                color: "#2563EB",
                fontWeight: 500,
              }}
            >
              Verified
            </span>
          </div>
          <span
            style={{
              fontSize: 12,
              color: "#6B7280",
            }}
          >
            {post.specialty}
          </span>
        </div>
      </div>

      {/* Follow button – compact like IG */}
      {user && (activeTab === "explore" || !isFollowing) && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onFollowDoctor();
          }}
          onMouseEnter={() => setIsFollowHovered(true)}
          onMouseLeave={() => setIsFollowHovered(false)}
          disabled={followLoading}
          style={{
            borderRadius: 999,
            padding: "6px 12px",
            fontSize: 12,
            fontWeight: 600,
            border: isFollowing
              ? `1px solid ${
                  isFollowHovered ? "#EF4444" : "#4F46E5"
                }`
              : "none",
            background: isFollowing
              ? isFollowHovered
                ? "rgba(239, 68, 68, 0.06)"
                : "rgba(79, 70, 229, 0.06)"
              : "linear-gradient(135deg, #4F46E5, #7C3AED)",
            color: isFollowing
              ? isFollowHovered
                ? "#EF4444"
                : "#4F46E5"
              : "#fff",
            cursor: followLoading ? "wait" : "pointer",
            opacity: followLoading ? 0.7 : 1,
            minWidth: 80,
          }}
        >
          {followLoading
            ? "..."
            : isFollowing
            ? isFollowHovered
              ? "Unfollow"
              : "Following"
            : "Follow"}
        </button>
      )}
    </div>
  );
};

/* ------------ Description / caption ------------ */

const PostContent = ({ post, isExpanded, onToggleExpanded }) => {
  const shouldTruncate = (post.description?.length || 0) > 140;
  const text = post.description || "";

  return (
    <div
      style={{
        marginTop: 6,
        fontSize: 14,
        lineHeight: 1.5,
        color: "#111827",
      }}
    >
      <span style={{ fontWeight: 600 }}>
        Dr. {post.doctorName}{" "}
      </span>
      <span>
        {isExpanded || !shouldTruncate
          ? text
          : `${text.slice(0, 140)}...`}
      </span>
      {shouldTruncate && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onToggleExpanded(post.id);
          }}
          style={{
            marginLeft: 4,
            border: "none",
            background: "transparent",
            padding: 0,
            fontSize: 13,
            fontWeight: 600,
            color: "#4F46E5",
            cursor: "pointer",
          }}
        >
          {isExpanded ? "less" : "more"}
        </button>
      )}
    </div>
  );
};

/* ------------ Footer: like / comment / share row ------------ */

const PostFooter = ({
  likes,
  commentCount,
  showComments,
  onToggleLike,
  onToggleComments,
  onShare,
  likeLoading,
}) => {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-start",
        gap: 12,
      }}
    >
      <IconButton
        ariaLabel="Like"
        icon={likes.isLiked ? "♥" : "♡"}
        active={likes.isLiked}
        activeColor="#EF4444"
        onClick={onToggleLike}
        disabled={likeLoading}
      />

      <IconButton
        ariaLabel="Comments"
        icon="💬"
        active={showComments}
        activeColor="#4F46E5"
        onClick={onToggleComments}
      />

      <IconButton
        ariaLabel="Share"
        icon="↗"
        onClick={onShare}
      />
    </div>
  );
};

const IconButton = ({
  icon,
  ariaLabel,
  onClick,
  disabled,
  active,
  activeColor = "#4F46E5",
}) => {
  const [hover, setHover] = React.useState(false);
  const color = active
    ? activeColor
    : hover
    ? "#111827"
    : "#374151";

  return (
    <button
      aria-label={ariaLabel}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      disabled={disabled}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        width: 32,
        height: 32,
        borderRadius: 999,
        border: "none",
        background: "transparent",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: disabled ? "wait" : "pointer",
        fontSize: 18,
        color,
        transition:
          "transform 0.15s ease, color 0.15s ease",
        transform: hover ? "scale(1.05)" : "scale(1)",
        padding: 0,
      }}
    >
      {icon}
    </button>
  );
};

/* ------------ Comments block ------------ */

const CommentsBlock = ({
  comments,
  user,
  post,
  newComment,
  commentLoading,
  onChangeComment,
  onAddComment,
  onDeleteComment,
  onCommentKeyPress,
}) => {
  return (
    <div
      style={{
        marginTop: 8,
      }}
    >
      {comments?.map((c) => (
        <div
          key={c.id}
          style={{
            marginBottom: 6,
            fontSize: 13,
            color: "#111827",
            display: "flex",
            justifyContent: "space-between",
            gap: 8,
          }}
        >
          <div>
            <span style={{ fontWeight: 600 }}>
              {c.authorName}
            </span>{" "}
            <span>{c.text}</span>
          </div>
          {user?.uid === c.authorId && (
            <button
              style={{
                border: "none",
                background: "transparent",
                color: "#EF4444",
                fontSize: 11,
                cursor: "pointer",
              }}
              onClick={() =>
                onDeleteComment(c.id, c.authorId)
              }
            >
              Delete
            </button>
          )}
        </div>
      ))}

      <div
        style={{
          marginTop: 8,
          display: "flex",
          gap: 6,
          alignItems: "flex-end",
        }}
      >
        <textarea
          rows={1}
          value={newComment || ""}
          onChange={(e) => onChangeComment(e.target.value)}
          onKeyDown={onCommentKeyPress}
          placeholder="Add a comment…"
          style={{
            flex: 1,
            resize: "none",
            borderRadius: 999,
            border: "1px solid #E5E7EB",
            padding: "6px 12px",
            fontSize: 13,
            maxHeight: 72,
          }}
        />
        <button
          disabled={commentLoading}
          onClick={onAddComment}
          style={{
            borderRadius: 999,
            border: "none",
            padding: "6px 10px",
            fontSize: 13,
            fontWeight: 600,
            background: "transparent",
            color: commentLoading
              ? "#9CA3AF"
              : "#4F46E5",
            cursor: commentLoading ? "wait" : "pointer",
          }}
        >
          Post
        </button>
      </div>
    </div>
  );
};

export default PostCard;
